import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { chargerAPI } from '#/api/charger'
import { EyeSlashIcon } from '@heroicons/react/20/solid'
import { EyeIcon } from 'lucide-react'
import { useState } from 'react'
import { organizationAPI } from '#/api/organization'


interface FormInputs {
  serialNumber: string
  vendor: string
  model: string
  initialFirmwareVersion: string
  wifiSSID: string
  wifiPassword: string
  setupUrl: string
  setupAdminPassword: string
  cleverevConnectionId: string
  cleverevConnectionUrl: string
  cleverevStickerId: string
  cleverevRfidFriendlyId: string
}

export default function NewChargerModal() {
  const { t } = useTranslation([ 'chargers', 'general' ])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }

  const [ showAdminPassword, setShowAdminPassword ] = useState(false)
  const [ showWifiPassword, setShowWifiPassword ] = useState(false)
  const close = () => navigate('..')

  // Fetch the organization for longId
  const { data: organization } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization ],
    queryFn: () => organizationAPI.get(params.organization)
  })

  const form = useForm<FormInputs>({
    defaultValues: {
      serialNumber: '',
      vendor: '',
      model: '',
      initialFirmwareVersion: '',
      wifiSSID: '',
      wifiPassword: '',
      setupUrl: '',
      setupAdminPassword: '',
      cleverevConnectionId: '',
      cleverevConnectionUrl: '',
      cleverevStickerId: '',
      cleverevRfidFriendlyId: ''
    }
  })

  // Mutation to create charger
  const mutation = useMutation({
    mutationFn: (data: FormInputs) => chargerAPI.create({
      organizationId: organization.id,
      ...data,
      vendor: data.vendor === '' ? null : data.vendor,
      model: data.model === '' ? null : data.model,
      cleverevConnectionId: data.cleverevConnectionId === '' ? null : data.cleverevConnectionId,
      cleverevConnectionUrl: data.cleverevConnectionUrl === '' ? null : data.cleverevConnectionUrl,
      cleverevStickerId: data.cleverevStickerId === '' ? null : data.cleverevStickerId,
      cleverevRfidFriendlyId: data.cleverevRfidFriendlyId === '' ? null : data.cleverevRfidFriendlyId
    }),
    onSuccess: created => {
      queryClient.setQueryData(
        [ 'organizations', params.organization, 'chargers', created.serialNumber ],
        created
      )
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent className='max-h-[80%] overflow-y-scroll'>
        <DialogHeader>
          <DialogTitle>{t('new_charger.title')}</DialogTitle>
          <DialogDescription>{t('new_charger.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(values => mutation.mutate(values))}
            className="flex flex-col gap-4"
            autoComplete="off"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('new_charger.errors.default'),
                  forbidden: t('new_charger.errors.forbidden')
                })}
              />
            )}

            {/* Serial Number */}
            <FormField
              name="serialNumber"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.serialNumber')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vendor */}
            <FormField
              name="vendor"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.vendor')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Model */}
            <FormField
              name="model"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.model')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Initial Firmware Version */}
            <FormField
              name="initialFirmwareVersion"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.initialFirmwareVersion')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Wifi SSID */}
            <FormField
              name="wifiSSID"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.wifiSSID')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Wifi Password */}
            <FormField
              name="wifiPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.wifiPassword')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        required
                        maxLength={128}
                        autoComplete="new-password"
                        type={showWifiPassword ? 'text' : 'password'}
                        {...field}
                        className="pr-10" // extra space for icon
                      />
                      <button
                        type="button"
                        onClick={() => setShowWifiPassword(prev => !prev)}
                        className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                        tabIndex={-1}
                      >
                        {showWifiPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Setup Url */}
            <FormField
              name="setupUrl"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.setupUrl')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Setup Admin Password */}
            <FormField
              name="setupAdminPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.setupAdminPassword')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        required
                        maxLength={128}
                        autoComplete="new-password"
                        type={showAdminPassword ? 'text' : 'password'}
                        {...field}
                        className="pr-10" // extra space for icon
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdminPassword(prev => !prev)}
                        className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                        tabIndex={-1}
                      >
                        {showAdminPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Connection ID */}
            <FormField
              name="cleverevConnectionId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.cleverevConnectionId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Connection Url */}
            <FormField
              name="cleverevConnectionUrl"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.cleverevConnectionUrl')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sticker ID */}
            <FormField
              name="cleverevStickerId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.cleverevStickerId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Friendly ID */}
            <FormField
              name="cleverevRfidFriendlyId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.cleverevRfidFriendlyId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('new_charger.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
